import { observable, action, toJS, computed } from 'mobx'
import { IContractTerm, ITerm, ITermNoPage } from '../types'
import { getExchangeRate } from '../utils/currencyUtil'
import { Decimal } from 'decimal.js'

class ContractStore {
    @observable public showSubmitModal: boolean = false
    @observable public loading: boolean = true
    @observable public id: number = 0
    @observable public status: number = 0
    @observable public approver: string = ''
    @observable public submitter: string = ''
    @observable public assignment: ITerm<string> = { page_num: 0, content: '', validated: true }
    @observable public change_of_control: ITerm<string> = { page_num: 0, content: '', validated: true }
    @observable public confidentiality: ITerm<string> = { page_num: 0, content: '', validated: true }
    // @observable public contract_sum: ITerm<number | string> = { page_num: 0, content: '', validated: true }
    @observable public original_sum: ITerm<number | string> = { page_num: 0, content: '', validated: true }
    @observable public counterparties: ITerm<string[]> = { page_num: 0, content: [], validated: true }
    @observable public dispute_resolution: ITerm<string> = { page_num: 0, content: '', validated: true }
    @observable public contract_name: ITerm<string> = { page_num: 0, content: '', validated: true }
    @observable public governing_law: ITerm<string> = { page_num: 0, content: '', validated: true }
    @observable public hkex_group_entities: ITerm<string[]> = { page_num: 0, content: [], validated: true }
    @observable public indemnity: ITerm<string> = { page_num: 0, content: '', validated: true }
    @observable public signed_date: ITermNoPage<string> = { content: '', validated: true }
    @observable public effective_date: ITermNoPage<string> = { content: '', validated: true }
    @observable public terminated_date_start: ITermNoPage<string> = { content: '', validated: true }
    @observable public terminated_date_end: ITermNoPage<string> = { content: '', validated: true }

    @observable public processApprove: boolean = false
    @observable public processReject: boolean = false

    @computed public get contract_sum() {
        const currency: any = this.original_sum.currency
        const sum = this.original_sum.content
        const rate = getExchangeRate(currency)
        if (!isNaN(parseFloat(sum as string))) {
            // return rate * 10 * parseFloat(sum as string) / 10.0
            // return math.multiply(math.bignumber(rate), math.bignumber(sum))
            return new Decimal(sum).mul(rate).toFixed(2)
        } else {
            return 'N/A'
        }
    }

    @computed public get contract_terms() {
        return {
            hkex_group_entities: this.hkex_group_entities.content,
            counterparties: this.counterparties.content,
            contract_name: this.contract_name.content,
            original_sum: this.original_sum.content,
            currency: this.original_sum.currency,
            governing_law: this.governing_law.content,
            dispute_resolution: this.dispute_resolution.content,
            confidentiality: this.confidentiality.content,
            change_of_control: this.change_of_control.content,
            assignment: this.assignment.content,
            indemnity: this.indemnity.content,
            signed_date: this.signed_date.content,
            effective_date: this.effective_date.content,
            terminated_date_start: this.terminated_date_start.content,
            terminated_date_end: this.terminated_date_end.content,
        }
    }

    @computed public get allNone() {
        return this.hkex_group_entities.content.length === 1 && this.hkex_group_entities.content[0] === 'N/A'
            && this.counterparties.content.length === 1 && this.counterparties.content[0] === 'N/A'
            && this.contract_name.content === 'N/A'
            && this.original_sum.content === 'N/A'
            && this.governing_law.content === 'N/A'
            && this.dispute_resolution.content === 'N/A'
            && this.confidentiality.content === 'N/A'
            && this.change_of_control.content === 'N/A'
            && this.assignment.content === 'N/A'
            && this.indemnity.content === 'N/A'
    }

    @action
    public clear() {
        this.showSubmitModal = false
        this.loading = true
        this.id = 0
        this.status = 0
        this.approver = ''
        this.submitter = ''
        this.assignment = { page_num: 0, content: '', validated: true }
        this.change_of_control = { page_num: 0, content: '', validated: true }
        this.confidentiality = { page_num: 0, content: '', validated: true }
        this.original_sum = { page_num: 0, content: '', validated: true }
        this.counterparties = { page_num: 0, content: [], validated: true }
        this.dispute_resolution = { page_num: 0, content: '', validated: true }
        this.contract_name = { page_num: 0, content: '', validated: true }
        this.governing_law = { page_num: 0, content: '', validated: true }
        this.hkex_group_entities = { page_num: 0, content: [], validated: true }
        this.indemnity = { page_num: 0, content: '', validated: true }
        this.signed_date = { content: '', validated: true }
        this.effective_date = { content: '', validated: true }
        this.terminated_date_start = { content: '', validated: true }
        this.terminated_date_end = { content: '', validated: true }
    }

    @action
    public resetStore() {
        this.setLoading(true)
        this.setStatus(0)
        this.setSignedDate('')
        this.setEffectiveDate('')
        this.setTerminatedDates(['', ''])
    }

    @action
    public setLoading(status: boolean) {
        this.loading = status
    }

    @action
    public setStatus(value: number) {
        this.status = value
    }

    @action
    public setApprover(username: string) {
        this.approver = username
    }

    @action
    public setSubmitter(username: string) {
        this.submitter = username
    }

    @action
    public setSubmitModalStatus(status: boolean) {
        this.showSubmitModal = status
    }

    @action
    public setContractTerm(id: number, name: string, contract_term: IContractTerm, status = 0, submitter = '', approver: '') {
        this.setLoading(false)
        this.setId(id)
        this.setStatus(status)
        this.setSubmitter(submitter)
        this.setApprover(approver)
        this.setAssignment(contract_term.assignment, contract_term.assignment_page)
        this.setChangeOfControl(contract_term.change_of_control, contract_term.change_of_control_page)
        this.setConfidentiality(contract_term.confidentiality, contract_term.confidentiality_page)
        this.setOriginalSum(contract_term.original_sum, contract_term.currency, contract_term.contract_sum_page)
        this.setCounterparties(contract_term.counterparties, contract_term.counterparties_page)
        this.setDisputeResolution(contract_term.dispute_resolution, contract_term.dispute_resolution_page)
        this.setContractName(name, contract_term.contract_name_page)
        this.setGoverningLaw(contract_term.governing_law, contract_term.governing_law_page)
        this.setHkexGroupEntities(contract_term.hkex_group_entities, contract_term.hkex_group_entities_page)
        this.setIndemnity(contract_term.indemnity, contract_term.indemnity_page)
        if (contract_term.effective_date) {
            this.setEffectiveDate(contract_term.effective_date)
        }
        if (contract_term.signed_date) {
            this.setSignedDate(contract_term.signed_date)
        }
        if (contract_term.terminated_date_start) {
            this.setTerminatedStartDate(contract_term.terminated_date_start)
        }
        if (contract_term.terminated_date_end) {
            this.setTerminatedEndDate(contract_term.terminated_date_end)
        }
    }

    @action
    public setId(id: number) {
        this.id = id
    }

    @action
    public setAssignment(assignment: string, page_num?: number) {
        this.assignment.content = assignment
        if (page_num) {
            this.assignment.page_num = page_num
        }
    }

    @action
    public setChangeOfControl(change_of_control: string, page_num?: number) {
        this.change_of_control.content = change_of_control
        if (page_num) {
            this.change_of_control.page_num = page_num
        }
    }

    @action
    public setConfidentiality(confidentiality: string, page_num?: number) {
        this.confidentiality.content = confidentiality
        if (page_num) {
            this.confidentiality.page_num = page_num
        }
    }

    @action
    public setOriginalSum(original_sum: string | string, currency?: string, page_num?: number) {

        const parseNum = parseFloat(original_sum)
        if (!isNaN(parseNum)) {
            this.original_sum.content = new Decimal(original_sum).toFixed(2)
        } else {
            this.original_sum.content = original_sum
        }

        if (currency) {
            this.original_sum.currency = currency
        }
        if (page_num) {
            this.original_sum.page_num = page_num
        }
    }

    @action
    public setHkexGroupEntities(hkex_group_entities: string[] | string, page_num?: number) {
        if (typeof hkex_group_entities === 'string') {
            this.hkex_group_entities.content = hkex_group_entities.split('\n')
        } else {
            this.hkex_group_entities.content = hkex_group_entities as string[]
        }

        if (page_num) {
            this.hkex_group_entities.page_num = page_num
        }
    }

    @action
    public setCounterparties(counterparties: string[] | string, page_num?: number) {
        if (typeof counterparties === 'string') {
            this.counterparties.content = counterparties.split('\n')
        } else {
            this.counterparties.content = counterparties
        }
        if (page_num) {
            this.counterparties.page_num = page_num
        }
    }

    @action
    public setDisputeResolution(dispute_resolution: string, page_num?: number) {
        this.dispute_resolution.content = dispute_resolution
        if (page_num) {
            this.dispute_resolution.page_num = page_num
        }
    }

    @action
    public setContractName(contract_name: string, page_num?: number) {
        this.contract_name.content = contract_name
        if (page_num) {
            this.contract_name.page_num = page_num
        }
    }

    @action
    public setEffectiveDate(effective_date: string) {
        this.effective_date.content = effective_date
    }

    @action
    public setGoverningLaw(governing_law: string, page_num?: number) {
        this.governing_law.content = governing_law
        if (page_num) {
            this.governing_law.page_num = page_num
        }
    }

    @action
    public setIndemnity(indemnity: string, page_num?: number) {
        this.indemnity.content = indemnity
        if (page_num) {
            this.indemnity.page_num = page_num
        }
    }

    @action
    public setSignedDate(signed_date: string) {
        this.signed_date.content = signed_date
    }

    @action
    public setTerminatedDates(dates: string[]) {
        this.terminated_date_start.content = dates[0]
        this.terminated_date_end.content = dates[1]
    }

    @action
    public setTerminatedStartDate(date: string) {
        this.terminated_date_start.content = date
    }

    @action
    public setTerminatedEndDate(date: string) {
        this.terminated_date_end.content = date
    }

    public getSubmitFormData() {
        const data = {
            id: toJS(this.id),
            approver_id: 0,
            change_of_control: this.change_of_control.content,
            confidentiality: this.confidentiality.content,
            assignment: this.assignment.content,
            contract_sum: this.contract_sum,
            original_sum: this.original_sum.content,
            counterparties: this.counterparties.content,
            dispute_resolution: this.dispute_resolution.content,
            contract_name: this.contract_name.content,
            governing_law: this.governing_law.content,
            hkex_group_entities: this.hkex_group_entities.content,
            indemnity: this.indemnity.content,
            signed_date: this.signed_date.content,
            effective_date: this.effective_date.content,
            terminated_date_start: this.terminated_date_start.content,
            terminated_date_end: this.terminated_date_end.content,
        }
        return data
    }
}

export default ContractStore
